import { useState, useEffect, useCallback } from 'react';
import useFilterPersistence from '../../../hooks/useFilterPersistence';
import { executeAnalyticsQuery, getAnalyticsTables, getClients, getSalesReps, getCarriers, getIncidentTypes, getTimeSlots, getClientOrigins, getPaymentMethods, getClientById, getAvailableDateColumns, getProductCategories, getFurnitureModelsByType, getModelColorsByType, getModelSizesByType } from '../../../api';

export const useAnalytics = () => {
  const defaultQuery = {
    metricColumn: 'TotalAmount',
    dimension: 1,
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    dateColumn: 0,
    filterGroups: [],
    orderBy: 1,
    timeFrame: 0,
    timeFrameOrderBy: 1,
    sortColumn: null,
    sortDirection: 'asc'
  };

  const [query, setQuery] = useFilterPersistence('filters_analytics', defaultQuery);
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedTableInfo, setSelectedTableInfo] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [clientNames, setClientNames] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(process.env.NODE_ENV === 'development' ? 10 : 50);
  const [totalCount, setTotalCount] = useState(0);
  const [executedDimension, setExecutedDimension] = useState(null);
  const [executedTimeFrame, setExecutedTimeFrame] = useState(null);
  const [executedMetric, setExecutedMetric] = useState(null);
  const [executedMetricColumn, setExecutedMetricColumn] = useState(null);
  const [dateColumns, setDateColumns] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadTables();
    loadDropdownOptions();
    loadDateColumns();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      const tableInfo = tables.find(t => t.name === selectedTable);
      setSelectedTableInfo(tableInfo);
    } else {
      setSelectedTableInfo(null);
    }
  }, [selectedTable, tables]);

  const loadTables = async () => {
    try {
      const tablesData = await getAnalyticsTables();
      setTables(tablesData);
      const ordersTable = tablesData.find(t => t.name.toLowerCase() === 'orders');
      if (ordersTable) {
        setSelectedTable(ordersTable.name);
      } else if (tablesData.length > 0) {
        setSelectedTable(tablesData[0].name);
      }
    } catch (err) {
      console.error('Error loading tables:', err);
    }
  };

  const loadDropdownOptions = async () => {
    try {
      const [salesReps, carriers, incidentTypes, timeSlots, clientOrigins, paymentMethods, productCategories, furnitureModels, modelColors, modelSizes] = await Promise.all([
        getSalesReps(),
        getCarriers(),
        getIncidentTypes(),
        getTimeSlots(),
        getClientOrigins(),
        getPaymentMethods(),
        getProductCategories(),
        getFurnitureModelsByType(),
        getModelColorsByType(),
        getModelSizesByType()
      ]);
      
      setDropdownOptions({
        SalesRepId: salesReps,
        CarrierId: carriers,
        IncidentTypeId: incidentTypes,
        TimeSlotId: timeSlots,
        ClientOriginId: clientOrigins,
        PaymentMethodId: paymentMethods,
        ProductCategoryId: productCategories,
        ProductModel: furnitureModels,
        ProductColor: modelColors,
        ProductSize: modelSizes
      });
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    }
  };

  const loadDateColumns = async () => {
    try {
      const columns = await getAvailableDateColumns();
      setDateColumns(columns);
    } catch (error) {
      console.error('Error loading date columns:', error);
    }
  };

  const loadClientName = async (clientId) => {
    if (!clientId || clientNames[clientId]) return;
    
    try {
      const client = await getClientById(clientId);
      setClientNames(prev => ({
        ...prev,
        [clientId]: `${client.name} ${client.lastName1}`.trim()
      }));
    } catch (error) {
      console.error('Error loading client:', error);
      setClientNames(prev => ({
        ...prev,
        [clientId]: `Cliente ID: ${clientId}`
      }));
    }
  };

  const executeQuery = async (page = 1, customItemsPerPage = null, customQuery = null) => {
    setLoading(true);
    setError('');
    try {
      const pageSize = customItemsPerPage || itemsPerPage;
      const queryToUse = customQuery || query;
      const queryWithPagination = {
        ...queryToUse,
        page,
        pageSize
      };
      const response = await executeAnalyticsQuery(queryWithPagination);
      
      if (response && typeof response === 'object') {
        if (response.items && Array.isArray(response.items)) {
          setResults(response.items);
          setTotalCount(response.totalCount || response.items.length);
        } else if (Array.isArray(response)) {
          setResults(response);
          setTotalCount(response.length);
        } else {
          setResults([response]);
          setTotalCount(1);
        }
      } else {
        setResults([]);
        setTotalCount(0);
      }
      
      setCurrentPage(page);
      if (customItemsPerPage) {
        setItemsPerPage(customItemsPerPage);
      }
      setExecutedDimension(queryToUse.dimension);
      setExecutedTimeFrame(queryToUse.timeFrame);
      setExecutedMetricColumn(queryToUse.metricColumn);
    } catch (err) {
      setError(err.message || 'Error al ejecutar la consulta');
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getAllData = async () => {
    const allData = [];
    let page = 1;
    const pageSize = 1000;
    let hasMoreData = true;

    while (hasMoreData) {
      try {
        const queryWithPagination = {
          ...query,
          page,
          pageSize
        };
        const response = await executeAnalyticsQuery(queryWithPagination);
        
        let pageData = [];
        if (response && typeof response === 'object') {
          if (response.items && Array.isArray(response.items)) {
            pageData = response.items;
          } else if (Array.isArray(response)) {
            pageData = response;
          } else {
            pageData = [response];
          }
        }
        
        if (pageData.length > 0) {
          allData.push(...pageData);
          hasMoreData = pageData.length === pageSize;
          page++;
        } else {
          hasMoreData = false;
        }
      } catch (error) {
        console.error('Error fetching page:', page, error);
        hasMoreData = false;
      }
    }

    return allData;
  };

  const resetAll = useCallback(() => {
    setQuery(defaultQuery);
    setResults([]);
    setExpandedRows(new Set());
    setCurrentPage(1);
    setTotalCount(0);
    setExecutedDimension(null);
    setExecutedTimeFrame(null);
    setExecutedMetric(null);
    setExecutedMetricColumn(null);
    setIsExporting(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // State
    query,
    results,
    loading,
    error,
    tables,
    selectedTable,
    selectedTableInfo,
    dropdownOptions,
    clientNames,
    currentPage,
    itemsPerPage,
    totalCount,
    executedDimension,
    executedTimeFrame,
    executedMetric,
    executedMetricColumn,
    dateColumns,
    expandedRows,
    isExporting,
    
    // Setters
    setQuery,
    setSelectedTable,
    setCurrentPage,
    setItemsPerPage,
    setExpandedRows,
    setIsExporting,
    setClientNames,
    
    // Actions
    executeQuery,
    getAllData,
    resetAll,
    loadClientName
  };
};